import { StateManager } from '../core/StateManager';
import { RulesEngine, type RuleResult } from '../services/predictive/RulesEngine';
import { AnomalyDetector, type AnomalyResult } from '../services/predictive/AnomalyDetector';
import { RULCalculator, type RULResult } from '../services/predictive/RULCalculator';
import { DRIVE_PROFILES } from '../config/profiles';
import type { DriveProfile } from '../types/drive';
import { escapeHtml } from '../utils/sanitize';

interface PartBOMItem {
  component: string;
  partCode: string;
  label: string;
  quantity: number;
  status: string;
}

interface PanelElements {
  rulFanMain: HTMLElement;
  rulFanControl: HTMLElement;
  rulCapacitor: HTMLElement;
  rulIGBT: HTMLElement;
  rulBearing: HTMLElement;
  anomaliesList: HTMLElement;
  rulesList: HTMLElement;
  healthScore: HTMLElement;
  lastUpdated: HTMLElement;
  partsBOMList: HTMLElement;
}

export class PredictivePanel {
  private state: StateManager;
  private rulesEngine: RulesEngine;
  private anomalyDetector: AnomalyDetector;
  private rulCalculator: RULCalculator;
  private elements: PanelElements;
  private intervalId: number | null = null;

  constructor(
    state: StateManager,
    elements: PanelElements,
  ) {
    this.state = state;
    this.rulesEngine = new RulesEngine();
    this.anomalyDetector = new AnomalyDetector();
    this.rulCalculator = new RULCalculator();
    this.elements = elements;

    state.subscribe('currentFreq', () => this.updateAnomalies());
    state.subscribe('heatsinkTemp', () => this.updateAnomalies());
    state.subscribe('currentAmp', () => this.updateAnomalies());
    state.subscribe('selectedProfileKey', () => this.onProfileChanged());
  }

  private getProfile(): DriveProfile | undefined {
    return DRIVE_PROFILES[this.state.get('selectedProfileKey')];
  }

  private onProfileChanged(): void {
    const profile = this.getProfile();
    if (profile) {
      this.rulCalculator.setProfile(profile);
    }
  }

  start(intervalMs = 3000): void {
    this.updateAll();
    this.intervalId = window.setInterval(() => this.updateAll(), intervalMs);
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private updateAll(): void {
    this.updateRUL();
    this.updatePartsBOM();
    this.updateRules();
    this.updateHealthScore();
    this.updateLastUpdated();
  }

  private updateRUL(): void {
    const fanMainHours = this.state.get('fanMainHours');
    const fanControlHours = this.state.get('fanControlHours');
    const daysOffline = this.state.get('daysOffline');

    const fanMain = this.rulCalculator.calculateFanMain(fanMainHours);
    const fanCtrl = this.rulCalculator.calculateFanControl(fanControlHours);
    const cap = this.rulCalculator.calculateCapacitor(daysOffline, 'normal', fanMainHours);

    const igbtTemp = this.state.get('heatsinkTemp');
    const profile = this.getProfile();
    const igbtMaxCycles = profile?.rulParams.igbtMaxCycles ?? 50000;
    const igbt = this.rulCalculator.calculateIGBT(fanMainHours, igbtMaxCycles, igbtTemp);

    const bearing = this.rulCalculator.calculateBearing(fanMainHours, 1.0, 1.0);

    this.renderRUL(this.elements.rulFanMain, fanMain);
    this.renderRUL(this.elements.rulFanControl, fanCtrl);
    this.renderRUL(this.elements.rulCapacitor, cap);
    this.renderRUL(this.elements.rulIGBT, igbt);
    this.renderRUL(this.elements.rulBearing, bearing);
  }

  private renderRUL(el: HTMLElement, result: RULResult): void {
    const color = result.status === 'critical' ? '#ff4757'
      : result.status === 'warn' ? '#ffb700'
      : '#00ff9d';

    el.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.2rem;">
        <span style="font-size:0.7rem;color:#8f9cae;">${escapeHtml(result.component)}</span>
        <span style="font-size:0.7rem;font-family:'Fira Code',monospace;color:${color};">
          ${result.remainingHours.toLocaleString()}h
        </span>
      </div>
      <div style="height:4px;background:#080c14;border-radius:2px;overflow:hidden;">
        <div style="height:100%;width:${Math.min(100, result.percentageUsed)}%;background:${color};border-radius:2px;transition:width 0.5s ease;"></div>
      </div>
      <div style="display:flex;justify-content:space-between;font-size:0.6rem;color:#5c6b80;margin-top:0.1rem;">
        <span>${result.percentageUsed.toFixed(0)}% usado</span>
        <span style="text-transform:uppercase;color:${color};">${escapeHtml(result.status)}</span>
      </div>
    `;
  }

  private updateRules(): void {
    const profile = this.getProfile();
    const ctx = {
      currentFreq: this.state.get('currentFreq'),
      currentAmp: this.state.get('currentAmp'),
      dcVoltage: this.state.get('dcVoltage'),
      heatsinkTemp: this.state.get('heatsinkTemp'),
      load: this.state.get('load'),
      imbalance: this.state.get('imbalance'),
      fanMainHours: this.state.get('fanMainHours'),
      fanControlHours: this.state.get('fanControlHours'),
      daysOffline: this.state.get('daysOffline'),
      voltageDrop: this.state.get('voltageDrop'),
      looseTerminal: this.state.get('looseTerminal'),
      cloggedHeatsink: this.state.get('cloggedHeatsink'),
      nominalCurrent: profile?.nominalCurrent ?? 0,
      nominalDC: profile?.nominalDC ?? 560,
      maxTemp: profile?.maxTemp ?? 85,
    };

    const results = this.rulesEngine.getTriggered(ctx);
    this.renderRules(results);
  }

  private renderRules(results: RuleResult[]): void {
    const triggered = results.filter((r) => r.triggered);
    if (triggered.length === 0) {
      this.elements.rulesList.innerHTML =
        '<div style="font-size:0.7rem;color:#5c6b80;padding:0.5rem 0;">Nenhuma regra disparada</div>';
      return;
    }

    this.elements.rulesList.innerHTML = triggered
      .map(
        (r) => `
      <div style="
        background:rgba(22,30,49,0.5);
        border-left:3px solid ${this.severityColor(r.severity)};
        border-radius:4px;
        padding:0.4rem 0.5rem;
        font-size:0.7rem;
        margin-bottom:0.3rem;
      ">
        <div style="display:flex;justify-content:space-between;">
          <strong style="color:${this.severityColor(r.severity)};">${escapeHtml(r.ruleName)}</strong>
          <span style="text-transform:uppercase;font-size:0.6rem;color:${this.severityColor(r.severity)};">${escapeHtml(r.severity)}</span>
        </div>
        <div style="color:#8f9cae;margin-top:0.15rem;">${escapeHtml(r.message)}</div>
      </div>
    `,
      )
      .join('');
  }

  private updateAnomalies(): void {
    const temp = this.state.get('heatsinkTemp');
    const current = this.state.get('currentAmp');
    const freq = this.state.get('currentFreq');
    const dcVoltage = this.state.get('dcVoltage');

    const anomalies: AnomalyResult[] = [
      this.anomalyDetector.detect('temp', temp),
      this.anomalyDetector.detect('current', current),
      this.anomalyDetector.detect('freq', freq),
      this.anomalyDetector.detect('dcVoltage', dcVoltage),
    ];

    const active = anomalies.filter((a) => a.isAnomaly);
    if (active.length === 0) {
      this.elements.anomaliesList.innerHTML =
        '<div style="font-size:0.7rem;color:#5c6b80;padding:0.5rem 0;">Nenhuma anomalia detectada</div>';
      return;
    }

    this.elements.anomaliesList.innerHTML = active
      .map(
        (a) => `
      <div style="
        background:rgba(255,71,87,0.1);
        border:1px solid rgba(255,71,87,0.3);
        border-radius:4px;
        padding:0.3rem 0.5rem;
        font-size:0.7rem;
        margin-bottom:0.3rem;
      ">
        <strong style="color:#ff4757;">${a.severity.toUpperCase()}</strong>
        <span style="color:#8f9cae;margin-left:0.3rem;">
          valor=${a.value.toFixed(1)} media=${a.mean.toFixed(1)} z-score=${a.zScore.toFixed(2)}
        </span>
      </div>
    `,
      )
      .join('');
  }

  private updateHealthScore(): void {
    const profile = this.getProfile();
    const fanMainLife = profile?.rulParams.fanMainLife ?? 50000;
    const fanControlLife = profile?.rulParams.fanControlLife ?? 30000;
    const fanMainUsage = this.state.get('fanMainHours') / fanMainLife;
    const fanControlUsage = this.state.get('fanControlHours') / fanControlLife;
    const score = Math.max(0, Math.round(100 - (fanMainUsage * 40 + fanControlUsage * 30)));

    this.elements.healthScore.innerHTML = `
      <div style="display:flex;align-items:center;gap:0.5rem;">
        <span style="font-size:1.2rem;font-weight:700;font-family:'Fira Code',monospace;color:${score > 70 ? '#00ff9d' : score > 40 ? '#ffb700' : '#ff4757'};">
          ${score}%
        </span>
        <span style="font-size:0.7rem;color:#8f9cae;">Saúde Geral</span>
      </div>
      <div style="height:6px;background:#080c14;border-radius:3px;overflow:hidden;margin-top:0.3rem;">
        <div style="height:100%;width:${score}%;background:${score > 70 ? '#00ff9d' : score > 40 ? '#ffb700' : '#ff4757'};border-radius:3px;transition:width 0.5s ease;"></div>
      </div>
    `;
  }

  private updatePartsBOM(): void {
    const profile = this.getProfile();
    if (!profile) {
      this.elements.partsBOMList.innerHTML = '';
      return;
    }

    const fanMainHours = this.state.get('fanMainHours');
    const fanControlHours = this.state.get('fanControlHours');
    const daysOffline = this.state.get('daysOffline');

    const fanMain = this.rulCalculator.calculateFanMain(fanMainHours);
    const fanCtrl = this.rulCalculator.calculateFanControl(fanControlHours);
    const cap = this.rulCalculator.calculateCapacitor(daysOffline, 'normal', fanMainHours);
    const igbtMaxCycles = profile?.rulParams.igbtMaxCycles ?? 50000;
    const igbtTemp = this.state.get('heatsinkTemp');
    const igbt = this.rulCalculator.calculateIGBT(fanMainHours, igbtMaxCycles, igbtTemp);
    const bearing = this.rulCalculator.calculateBearing(fanMainHours, 1.0, 1.0);

    const parts: PartBOMItem[] = [];

    if (fanMain.status !== 'ok' && profile.fanCode) {
      parts.push({ component: 'Ventilador Principal', partCode: profile.fanCode, label: 'Ventilador', quantity: profile.fanCount || 1, status: fanMain.status });
    }
    if (fanCtrl.status !== 'ok' && profile.fanCode) {
      parts.push({ component: 'Ventilador de Controle', partCode: profile.fanCode, label: 'Ventilador', quantity: 1, status: fanCtrl.status });
    }
    if (cap.status !== 'ok' && profile.capacitorBankCode) {
      parts.push({ component: 'Capacitor DC Link', partCode: profile.capacitorBankCode, label: 'Banco de Capacitores', quantity: 1, status: cap.status });
    }
    if (igbt.status !== 'ok' && profile.igbtModuleCode) {
      parts.push({ component: 'IGBTs', partCode: profile.igbtModuleCode, label: 'Módulo IGBT', quantity: 1, status: igbt.status });
    }
    if (bearing.status !== 'ok') {
      parts.push({ component: 'Rolamentos', partCode: '—', label: 'Rolamento do Motor', quantity: 1, status: bearing.status });
    }

    if (parts.length === 0) {
      this.elements.partsBOMList.innerHTML = '<div style="font-size:0.7rem;color:#5c6b80;padding:0.5rem 0;">Nenhuma peça necessária no momento.</div>';
      return;
    }

    this.elements.partsBOMList.innerHTML = `
      ${parts.map(p => `
        <div style="
          background:rgba(22,30,49,0.5);
          border-left:3px solid ${p.status === 'critical' ? '#ff4757' : '#ffb700'};
          border-radius:4px;
          padding:0.4rem 0.5rem;
          margin-bottom:0.3rem;
          font-size:0.7rem;
        ">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <strong style="color:${p.status === 'critical' ? '#ff4757' : '#ffb700'};font-size:0.65rem;">${escapeHtml(p.component)}</strong>
            <span style="font-size:0.6rem;color:#5c6b80;">qtd: ${p.quantity}</span>
          </div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:0.2rem;">
            <span style="color:#8f9cae;font-family:monospace;font-size:0.65rem;">${escapeHtml(p.partCode)}</span>
            <span style="font-size:0.6rem;color:#5c6b80;">${escapeHtml(p.label)}</span>
          </div>
        </div>
      `).join('')}
    `;
  }

  private updateLastUpdated(): void {
    this.elements.lastUpdated.textContent = new Date().toLocaleTimeString();
  }

  private severityColor(severity: string): string {
    switch (severity) {
      case 'critical': return '#ff4757';
      case 'warning': return '#ffb700';
      default: return '#00d9ff';
    }
  }
}
